import { ITuple3 } from '../component-data-structure';
import { ITransitionCallback, Transition } from './transition';

/**
 * Runs a group of transitions as one unit and reports when the group starts and finishes.
 *
 * Membership used to be tracked by two counters, which only works if every member starts exactly once
 * and finishes exactly once per run. Live preview breaks both halves of that assumption: an entity can be
 * deleted mid-run, a new one can be added mid-run, and — since PROJECT_JUTE_PREVIEW.md's restart rules —
 * a single member can be restarted on its own when its content is edited while it is in flight. A counter
 * cannot express "this particular one went round again", so membership is tracked per transition instead.
 */
export class TransitionManager<T extends number | ITuple3> {
    private _transitions: Transition<T>[]
    private _onStartAll?: ITransitionCallback<number>;
    private _onFinishAll?: ITransitionCallback<number>;
    /** Members that have started and not been removed. Identity, not a count — a member may restart. */
    private _started: Set<Transition<T>>;
    /** Members that have finished since they last started. */
    private _finished: Set<Transition<T>>;
    /** Whether `_onStartAll` / `_onFinishAll` have already fired for the current run. */
    private _startAllFired: boolean;
    private _finishAllFired: boolean;

    constructor(...args: Transition<T>[]) {
        this._transitions = [];
        //this._transitions = args;
        this._started = new Set();
        this._finished = new Set();
        this._startAllFired = false;
        this._finishAllFired = false;
    }

    /**
     * Register transitions and wire up the per-member bookkeeping.
     *
     * The bookkeeping callbacks are bound HERE rather than in `onStartAll` / `onFinishAll`, which is where
     * they used to be bound. Those two run once per scene-transition setup, so anything registered
     * afterwards — every entity added during a live preview session — was counted in `_transitions.length`
     * but could never report, and the group could then never reach "all started".
     */
    add(...args: Transition<T>[]): void {
        for (let i = 0; i < args.length; i++) {
            const transition = args[i];
            if (this._transitions.indexOf(transition) !== -1) continue;
            this._transitions.push(transition);
            transition.onStart(() => this._handleStart(transition));
            transition.onFinish(() => this._handleFinish(transition));
        }
    }

    /**
     * Drop transitions from the manager. The counterpart to `add`, which this class went without — the
     * `_transitions` array was push-only, so an entity that unmounted (deleted, or its scene torn down)
     * left a dead Transition behind forever. `startAll` then started it, and because `onStartAll` and
     * `onFinishAll` fire only once every member has reported, one orphan was enough to stop those
     * callbacks firing for the whole scene.
     *
     * Removing a member can be the event that completes a run — the group was only waiting on the entity
     * that just went away — so both group callbacks are re-evaluated afterwards.
     */
    remove(...args: Transition<T>[]): void {
        for (let i = 0; i < args.length; i++) {
            const index = this._transitions.indexOf(args[i]);
            if (index === -1) continue;
            this._transitions.splice(index, 1);
            this._started.delete(args[i]);
            this._finished.delete(args[i]);
        }
        this._checkStartAll();
        this._checkFinishAll();
    }

    resetManager(): void {
        this._started.clear();
        this._finished.clear();
        this._startAllFired = false;
        this._finishAllFired = false;
    }

    /** Begin a new run: every member restarts and both group callbacks are armed again. */
    startAll(): void {
        this.resetManager();
        for (let i = 0; i < this._transitions.length; i++) {
            this._transitions[i].start();
        }
    }

    /**
     * Restart every member WITHOUT re-announcing the run.
     *
     * For a live content edit that lands while the group is in flight. `startAll` would re-fire
     * `onStartAll`, and the scene hangs its enter-start side effects off that callback — the phase
     * dispatch, preset and model animations, and the onLoad `playSound` actions. Replaying a sound because
     * someone nudged an entity is not a transition restart, it is a scene restart. `onFinishAll` is armed
     * again, because the run genuinely is not over until the restarted members land.
     */
    restartAll(): void {
        this._finished.clear();
        this._finishAllFired = false;
        for (let i = 0; i < this._transitions.length; i++) {
            this._transitions[i].start();
        }
    }

    get numberTransitions(): number {
        return this._transitions.length
    }

    /** True while any member is mid-flight. Drives the restart-vs-store decision on a live content edit. */
    get isRunning(): boolean {
        for (let i = 0; i < this._transitions.length; i++) {
            if (this._transitions[i].isRunning) return true;
        }
        return false;
    }

    onStartAll(onStartAll?: () => any): void {
        if (!onStartAll) return;
        this._onStartAll = onStartAll;
    }

    onFinishAll(onFinishAll?: () => any): void {
        if (!onFinishAll) return;
        this._onFinishAll = onFinishAll;
    }

    private _handleStart(transition: Transition<T>): void {
        this._started.add(transition);
        // A member that had already finished is going round again, so the run is open once more.
        if (this._finished.delete(transition)) this._finishAllFired = false;
        this._checkStartAll();
    }

    private _handleFinish(transition: Transition<T>): void {
        // Ignore a finish from something that never started in this run — `stop()` and `forceFinish()`
        // both fire onFinish, and neither means the group made progress.
        if (!this._started.has(transition)) return;
        this._finished.add(transition);
        this._checkFinishAll();
    }

    private _checkStartAll(): void {
        if (this._startAllFired) return;
        if (!this._transitions.length || this._started.size !== this._transitions.length) return;
        this._startAllFired = true;
        this._onStartAll?.(this._transitions.length);
    }

    private _checkFinishAll(): void {
        if (this._finishAllFired) return;
        if (!this._started.size || this._finished.size !== this._started.size) return;
        this._finishAllFired = true;
        this._onFinishAll?.(this._started.size);
    }

}
